import { CITADEL_SERVER_MAP } from "@toruslabs/constants";
import { verify } from "@toruslabs/eccrypto";
import { get } from "@toruslabs/http-helpers";
import { base64ToBytes, keccak256Bytes, toBase64, utf8ToBytes } from "@toruslabs/metadata-helpers";
import { OriginData, WEB3AUTH_NETWORK_TYPE } from "@web3auth/auth";
import log from "loglevel";

import config from "@/config";

import { OPENLOGIN_DAPP_MODULE_KEY } from "./constants";

export function validateInternalHost(urlString: string): boolean {
  try {
    const allowedHostnames = ["localhost", "openlogin.com", "web3auth.io", "web3auth.vercel.app"];
    const url = new URL(urlString);
    let allowed = false;
    allowedHostnames.forEach((hostnameEnding) => {
      if ((url.hostname.endsWith(`.${hostnameEnding}`) || url.hostname === hostnameEnding) && (url.protocol === "https:" || url.protocol === "http:"))
        allowed = true;
    });
    return allowed;
  } catch (error) {
    log.error(error, "could not validate url");
    return false;
  }
}

export const isAllowedHosts = (originUrl: string): boolean => {
  return validateInternalHost(originUrl);
};

export const getWhitelist = async (clientId: string, network: WEB3AUTH_NETWORK_TYPE): Promise<OriginData> => {
  try {
    const url = new URL(`${CITADEL_SERVER_MAP[config.buildEnv]}/v1/signer/whitelist`);
    url.searchParams.append("project_id", clientId);
    url.searchParams.append("network", network ?? "");
    const res = await get<{ signed_urls: OriginData }>(url.href);
    return res.signed_urls;
  } catch (error: unknown) {
    // fail silently
    if (error instanceof Response) {
      log.error("error getting whitelist", (error as Response)?.status, JSON.stringify((error as Response)?.body));
    } else {
      log.error("error in whitelist", error);
    }
    return {};
  }
};

export async function validateRedirect(clientId: string, origin: string, originData: OriginData): Promise<void> {
  const originUrl = new URL(origin);
  if (isAllowedHosts(origin)) {
    return;
  }
  // skips validation for openlogin.
  if (clientId === OPENLOGIN_DAPP_MODULE_KEY && origin === window.location.origin) return;
  const b64OriginSig = originData[originUrl.origin];
  if (b64OriginSig) {
    const base64ClientId = toBase64(clientId);
    const base64OriginSig = toBase64(b64OriginSig);
    try {
      await verify(base64ToBytes(base64ClientId), keccak256Bytes(utf8ToBytes(origin)), base64ToBytes(base64OriginSig));
      return;
    } catch (error) {
      log.warn(error, "Normal casing origin check didn't work");
    }
    try {
      await verify(base64ToBytes(base64ClientId), keccak256Bytes(utf8ToBytes(origin.toLowerCase())), base64ToBytes(base64OriginSig));
      return;
    } catch (error) {
      log.warn(error, "lower casing origin check didn't work");
    }
  }

  throw new Error(
    `could not validate redirect, please whitelist your domain: ${origin} for provided clientId ${clientId} at https://dashboard.web3auth.io.`
  );
}
